import { BaseStore, getOrCreateStore } from '../di'
import { observable, computed, action } from 'mobx'
import { getIso15924Tag } from '@Locales'
import IntlMessageFormat from 'intl-messageformat'
import IntlRelativeFormat from 'intl-relativeformat'
import memoizeFormatConstructor from 'intl-format-cache'
import loadLocaleData from '@Locales/node/loadLocaleData'
import getMessages from '@Translations'

const getMessageFormat = memoizeFormatConstructor(IntlMessageFormat)
const getRelativeFormat = memoizeFormatConstructor(IntlRelativeFormat)

class LocaleStore extends BaseStore {
  DEFAULT_LOCALE = 'en'

  @observable _translations = observable.map()
  @observable currentLocale = 'en'
  @observable clientInit = false

  @computed
  get currentIso15924Tag() {
    return getIso15924Tag(this.currentLocale) || this.currentLocale
  }

  @computed
  get currentMessages() {
    const { currentLocale } = this
    return this.getMessages(currentLocale)
  }

  getMessages(locale) {
    const { _translations } = this
    return _translations.get(locale)
  }

  formatMessage(descriptor, values) {
    const { id, defaultMessage } = descriptor
    const messages = this.currentMessages
    const message = messages && messages[id]
    if (message) {
      return getMessageFormat(message, this.currentLocale).format(values)
    } else {
      return getMessageFormat(defaultMessage, this.currentLocale).format(values)
    }
  }

  formatRelative(value, opts = {}) {
    const date = new Date(value)
    const now = opts && opts.now ? new Date(opts.now) : new Date()
    return getRelativeFormat(this.currentIso15924Tag).format(date, { now })
  }

  @action
  setCurrent(locale) {
    this.currentLocale = locale || 'en'
  }

  @action
  reset() {
    this.currentLocale = this.DEFAULT_LOCALE
  }

  @action
  setClientInit() {
    this.clientInit = true
  }

  @action
  addMessages(locale, messages) {
    this._translations.set(locale, messages)
  }

  changeLocale = async (locale) => {
    try {
      await loadLocaleData(locale)
      this.addMessages(locale, this.getMessages(locale) || (await getMessages(locale)))
      this.setCurrent(locale)
    } catch (e) {
      console.error(e)
    }
  }

  serverInit = (locale, messages) => {
    this.setCurrent(locale)
    this.addMessages(locale, messages)
    this.setClientInit()
  }
}

export default getOrCreateStore('LocaleStore', LocaleStore)
